import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithTextGetter
} from 'components/final-form/dropdownUtils';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import { MessageSourceProps, withMessages } from 'react-message-source';
import React, { Component, ReactNode } from 'react';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import styled from 'styled-components';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Header, Loader, RadioProps } from 'semantic-ui-react';
import Radio from 'components/final-form/Radio';
import DropZone from 'components/dropzone/DropZone';
import { FileRejection } from 'react-dropzone';
import { FormApi } from 'final-form';
import { GuarantorDto, TariffDto, UpsertMisantoImportDto } from 'ts-types/api.types';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import { createMisatnoImport } from 'services/misantoImportServices';
import { getAllTariffs } from 'services/tariffServices';
import Select from 'components/final-form/Select';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import LoaderComponent from 'components/LoaderComponent';
import { searchGuarantorsForInput } from 'services/guarantorServices';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { getAllActiveTreaters } from 'services/treaterServices';

const StyledContainer = styled.div`
  height: 100%;
`;

const SearchButtonContainer = styled.div`
  flex: 1 1 100%;
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;


const SearchGroup = styled.div`
  flex: 0 1 auto;
  margin-left: 5rem;

  &.noMargin {
    margin-left: 0;
  }
`;

const StyledLabel = styled.label`
  display: inline-block;
  min-width: 40px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const FileUploadSearchGroup = styled.div`
  flex: 0 1 300px;
  margin-left: 5rem;

  &.noMargin {
    margin-left: 0;
  }
`;

const TariffSearchGroup = styled.div`
  flex: 0 1 300px;
  margin-left: 5rem;

  &.noMargin {
    margin-left: 0;
  }
`;

const GuarantorSearchGroup = styled.div`
  flex: 0 1 250px;
  margin-left: 5rem;

  &.noMargin {
    margin-left: 0;
  }
`;

const SearchField = styled.div`
  margin-bottom: 7px;
`;

interface Props extends AuthConsumerRenderProps, MessageSourceProps {

}

interface State {
  executionType: Array<RadioProps>,
  foundTariffs: Array<TariffDto>,
  loadingMisantoImportData?: string,
  treaterOptions: Array<DropdownOption>,
  submitting: boolean,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

// TODO: This should be generated
export enum ExecutionType {
  TEST_RUN = "TEST_RUN",
  IMPORT = "IMPORT",
  TEST_PATIENT = "TEST_PATIENT",
  IMPORT_PATIENT = "IMPORT_PATIENT",
  TEST_INTERVENTION = "TEST_INTERVENTION",
  IMPORT_INTERVENTION = "IMPORT_INTERVENTION",
  TEST_INVOICE = "TEST_INVOICE",
  IMPORT_INVOICE = "IMPORT_INVOICE",
  TEST_VACCINATION = "TEST_VACCINATION",
  IMPORT_VACCINATION = "IMPORT_VACCINATION"
}

class MisantoImportView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);
    const {getMessage} = this.props;
    const cancelTokenSource = axios.CancelToken.source();


    let executionTypeRadioProps = generateRadioButtonOptionsFromEnum(
        Object.values(ExecutionType), "executionType", getMessage);

    this.state = {
      executionType: executionTypeRadioProps,
      submitting: false,
      foundTariffs: [],
      treaterOptions: [],
      loadingMisantoImportData: getMessage("misantoImport.loader"),
      cancelTokenSource
    }

    this.fetchData();
  }

  fetchData = async () => {

    const {cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        loadingMisantoImportData: undefined
      })
    };

    try {
      const tarifs = await getAllTariffs(cancelTokenSource)
      const treaters = await getAllActiveTreaters(cancelTokenSource)

      this.setState({
        treaterOptions: mapToDropdownOptionArray(treaters, ["firstName", "name"], "id"),
        foundTariffs: tarifs
      });
    } finally {
      onFinally();
    }


  }

  handleError(error: any) {
    const {getMessage} = this.props;

    const status = error.status;
    const exception: string = error.exception;
    if (status === 500 && exception.includes("MultipartException")) {
      this.setErrorMessage(getMessage("misantoImport.error.fileSizeExceeded"))
      return;
    }

    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      "INVALID_DOCUMENT_INPUT"
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };



  handleSubmit = (values: Partial<UpsertMisantoImportDto>) => {

    if (this.state.submitting) {
      return;
    }

    const {cancelTokenSource} = this.state;
    const {getMessage, options, currentUser} = this.props;

    if (values.file && values.executionType) {
      this.setState({
        loadingMisantoImportData: getMessage("misantoImport.importing")
      });

      values.mandateId = currentUser!.userConfig.mandateId;
      values.userId = currentUser!.id;

      createMisatnoImport(values, cancelTokenSource)
          .then((response) => {

            this.setState({
              submitting: true
            })
            // TODO: not needed if the response is opened in new window
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers["content-disposition"];
            const filenameRegex = /filename=(.*)/;
            const regexResponse = filenameRegex.exec(contentDisposition);
            const defaultFileName = "response.txt";

            const fileName = regexResponse ?
                regexResponse[1] ? regexResponse[1] : defaultFileName :
                defaultFileName;

            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

          })
          .catch((e) => this.handleError(e.response.data))
          .finally(() => {
            this.setState({
              loadingMisantoImportData: undefined,
              submitting: false
            });
          });
    }

  };

  render = (): ReactNode => {
    const {executionType, foundTariffs, loadingMisantoImportData} = this.state;
    const {options, currentUser} = this.props;

    const defaultTariffId = foundTariffs && foundTariffs.length ? foundTariffs[0].id : undefined;

    const initialValues: Partial<UpsertMisantoImportDto> = {
      executionType: executionType[0].value!.toString(),
      normalTariffId: defaultTariffId,
      privateTariffId: defaultTariffId,
      mandateId: currentUser!.userConfig.mandateId,
      userId: currentUser!.id,
      treaterId: options.currentTreater.id
    };

    return (
        <PraxStyledSegment>
          <StyledContainer>
            <FinalForm
                onSubmit={this.handleSubmit}
                initialValues={initialValues}
                subscription={{values: true}}
                render={this.renderForm}
            />
          </StyledContainer>

          <StyledViewTableRow>
            {loadingMisantoImportData ? (<LoaderComponent message={loadingMisantoImportData}/>) : ""}
          </StyledViewTableRow>

        </PraxStyledSegment>
    );
  };

  renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {
    const {getMessage} = this.props;
    const {executionType, foundTariffs, errorMessage} = this.state;

    const uploadMessage = values.file ? values.file.name : getMessage("misantoImport.upload");

    const mappedTariffs = mapToDropdownOptionArray(foundTariffs, "description");

    return (
        <form onSubmit={handleSubmit}>
          {errorMessage
          && <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
          </StyledErrorMessage>
          }
          <StyledContainer>
            <Header>{getMessage("misantoImport")}</Header>
          </StyledContainer>
          <StyledContainer style={{marginTop: 10}}
                           onKeyDown={(e) => this.onEnterKeyPressed(e, values)}>
            <SearchOptionsContainer>
              <FileUploadSearchGroup className="noMargin">
                <Header size="tiny">{getMessage("misantoImport.treatment")}</Header>
                <SearchField>
                  <DropZone
                      multiple={false}
                      message={uploadMessage}
                      onDropAccepted={(acceptedFiles: File[]) => this.onDropAccepted(acceptedFiles, form)}
                      onDropRejected={this.onDropRejected}
                  />
                </SearchField>
              </FileUploadSearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("misantoImport.executionType")}</Header>
                <SearchField>
                  <Field
                      name="executionType"
                      component={Radio}
                      radioDefinition={executionType[0]}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="executionType"
                      component={Radio}
                      radioDefinition={executionType[1]}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="executionType"
                      component={Radio}
                      radioDefinition={executionType[2]}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="executionType"
                      component={Radio}
                      radioDefinition={executionType[3]}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="executionType"
                      component={Radio}
                      radioDefinition={executionType[4]}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="executionType"
                      component={Radio}
                      radioDefinition={executionType[5]}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="executionType"
                      component={Radio}
                      radioDefinition={executionType[6]}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="executionType"
                      component={Radio}
                      radioDefinition={executionType[7]}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="executionType"
                      component={Radio}
                      radioDefinition={executionType[8]}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="executionType"
                      component={Radio}
                      radioDefinition={executionType[9]}
                  />
                </SearchField>
              </SearchGroup>
              <TariffSearchGroup>
                <Header size="tiny">{getMessage("misantoImport.tariffs")}</Header>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("misantoImport.normalTariff")}:
                  </StyledLabel>
                  <Field
                      name="normalTariffId"
                      component={Select}
                      fluid
                      options={mappedTariffs}
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("misantoImport.privateTariff")}:
                  </StyledLabel>
                  <Field
                      name="privateTariffId"
                      component={Select}
                      fluid
                      options={mappedTariffs}
                  />
                </SearchField>
              </TariffSearchGroup>
              {this.renderGuarantorSearchGroup()}
              <SearchButtonContainer>
                <Button
                    type="submit" primary compact>
                  {getMessage("misantoImport.import")}
                </Button>
                {
                  this.state.submitting &&
                  <>
                    <Loader inline active size="small"/>
                    <>{getMessage("misanto.importing")}</>
                  </>
                }
              </SearchButtonContainer>
            </SearchOptionsContainer>
          </StyledContainer>
        </form>
    )
  };

  renderGuarantorSearchGroup = () => {
    const {getMessage} = this.props;
    const {treaterOptions} = this.state;
    return (
        <GuarantorSearchGroup>
          <Header size="tiny">{getMessage("misantoImport.guarantors")}</Header>
          <SearchField>
            <StyledLabel className="margin">
              {getMessage("misantoImport.patientReceiverGuarantor")}:
            </StyledLabel>
            <Field
                name="patientReceiverGuarantorId"
                component={Select}
                remoteSearch
                remoteSearchMethod={this.searchGuarantors}
                searchResultToOptionMapper={this.guarantorMapper}
                fluid
            />
          </SearchField>
          <SearchField>
            <StyledLabel className="margin">
              {getMessage("misantoImport.treater")}:
            </StyledLabel>
            <Field
                name="treaterId"
                component={Select}
                options={treaterOptions}
                fluid
            />
          </SearchField>
        </GuarantorSearchGroup>
    );
    // <SearchField>
    //   <StyledLabel className="margin">
    //     {getMessage("misantoImport.employerReceiverGuarantor")}:
    //   </StyledLabel>
    //   <Field
    //       name="employerReceiverGuarantorId"
    //       component={Select}
    //       validate={required}
    //       remoteSearch
    //       remoteSearchMethod={this.searchGuarantors}
    //       searchResultToOptionMapper={this.guarantorMapper}
    //       fluid
    //   />
    // </SearchField>

  };

  onEnterKeyPressed = (e: any, values: any) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      this.handleSubmit(values)
    }
  };

  onDropAccepted = (acceptedFiles: File[], form: FormApi) => {
    form.change("file", acceptedFiles[0]);
  };

  onDropRejected = (rejections: FileRejection[]) => {
    // TODO: Handle drop rejected
    console.log(rejections, "FILE REJECTIONS");
  };

  searchGuarantors = (search: string): Promise<Array<GuarantorDto>> => {

    const {cancelTokenSource} = this.state;

    return searchGuarantorsForInput(search, cancelTokenSource, 75);
  };

  guarantorMapper = (response: Array<GuarantorDto>): Array<DropdownOption> => {
    return mapToDropdownOptionArrayWithTextGetter(response,
        (guarantor: GuarantorDto) => {
          if (!guarantor.description) {
            return guarantor.bagNumber;
          }
          if (!guarantor.bagNumber) {
            return guarantor.description;
          }
          return `${guarantor.description} - ${guarantor.bagNumber}`;
        });
  };

}

export default withAuthContext(withMessages(MisantoImportView));

